import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { MediaRef, MessageDoc, ReportReason, UserDoc } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { initialsOf } from '../../components/common';
import { LockedMedia } from '../../components/LockedMedia';
import { MediaError, checkLimits, preparePostImage, probeVideo } from '../../lib/media';
import { ChatAttachButton, ChatAttachPanel, type ChatAttachment } from './ChatAttach';
import {
  moderationRefusal,
  mediaProRequired,
  type PreparedUpload,
  type SentMessage,
} from '../../lib/backend/types';

/**
 * ATTACHMENTS, AND THE ONE THING THAT MATTERS MOST ABOUT THEM.
 *
 * Sending a photo is Pro. OPENING a photo is Pro. Those are two different
 * people's subscriptions, and the gap between them is where this feature fails
 * silently: a Pro member sends a photo to a free member, the send succeeds, the
 * bubble looks right, and the photo lands as a blur the other person never
 * opens. Nobody is told. A conversation where one side does not know their
 * photo was never seen is worse than not allowing the photo at all.
 *
 * So the recipient's reach is stated TWICE, at two different confidence levels:
 *
 *   BEFORE — ChatAttachPanel warns that opening is Pro-only, before a file is
 *            picked. Unconditional, because at that point we do not know.
 *   AFTER  — `messageSend` returns `media.recipientCanView`, which is the real
 *            answer. This client CANNOT work it out for itself: firestore.rules
 *            denies a read of another member's entitlement document, and that
 *            is deliberate. The server answers once, at send time, and also
 *            persists it as `MessageDoc.mediaLockedFor` so scrolling back
 *            tomorrow still shows it.
 *
 * When there is no answer at all (demo mode, or a build predating the media
 * reply) the fallback is the honest unconditional sentence — never a guess
 * dressed up as a fact. `Backend.sendMessage` may resolve with void precisely
 * so that "no answer" cannot be mistaken for "yes they can".
 *
 * VIDEO IS CHECKED BY NOTHING, and every place a clip is sent or shown says so.
 * The moderation provider takes text and images; sampling one frame out of
 * fifteen seconds would be worse than honest silence, because it would let the
 * product claim coverage it does not have.
 */

/** Consecutive poll misses before the loop gives up and hands the user a retry. */
const MAX_LOAD_FAILURES = 3;

const errText = (e: unknown) =>
  String(e)
    .replace(/^(FirebaseError|Error):\s*/g, '')
    .slice(0, 200);

/** A destructive action waiting on the in-app confirmation sheet. */
type PendingAction = { body: string; cta: string; act: () => Promise<void> };

export function ConversationView() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [draft, setDraft] = useState('');
  const [other, setOther] = useState<UserDoc | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [stalled, setStalled] = useState(false);
  const [error, setError] = useState('');
  /** A moderation refusal — expected, explainable, and not a fault. */
  const [refused, setRefused] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [pendingBusy, setPendingBusy] = useState(false);
  /** The picked, resized, blur-hashed file — not uploaded until send. */
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [mediaBusy, setMediaBusy] = useState(false);
  /**
   * Anything the composer needs to say about an attachment — a limit refusal,
   * or whether the person on the other side can actually open what was just
   * sent. It renders NEXT TO THE COMPOSER rather than in the alert strip at the
   * top of the screen, and that placement is the point: sending scrolls the
   * thread to the bottom, so a banner at the top is a banner nobody reads, and
   * "your photo landed as a blur" is the one sentence that must not be missed.
   */
  const [composerNotice, setComposerNotice] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const failures = useRef(0);
  /** Mirror of the live preview URL, so unmounting cannot orphan a blob URL. */
  const previewRef = useRef<string | null>(null);
  /** Measured, not guessed — see the effect below. */
  const [composerHeight, setComposerHeight] = useState(0);

  const conv = app.conversations.find((c) => c.id === conversationId);
  const otherUid = conv?.members.find((m) => m !== app.uid) ?? '';
  const isPro = app.entitlement?.tier === 'pro';
  const otherName = other?.displayName || t('msg.media.someone');
  // Declared up here because an effect below reads it: a dependency array is
  // evaluated during render, so a `const` further down would be in its TDZ.
  const active = conv?.status === 'active';

  const load = useCallback(async () => {
    if (!conversationId) {
      setLoading(false); // no id to fetch: show the empty state, not a spinner forever
      return;
    }
    try {
      setMessages(await app.backend.listMessages(conversationId));
      failures.current = 0;
      setStalled(false);
    } catch (e) {
      // This used to have no catch at all, so one dropped connection became an
      // unhandled rejection every 4 seconds for as long as the screen was open.
      // Give up after a few misses and let the user restart it deliberately.
      console.warn(e);
      failures.current += 1;
      if (failures.current >= MAX_LOAD_FAILURES) setStalled(true);
    } finally {
      setLoading(false);
    }
  }, [app.backend, conversationId]);

  useEffect(() => {
    if (stalled) return; // polling stays off until the user taps retry
    load();
    const timer = setInterval(load, 4000); // lightweight fetch loop — no websockets needed yet
    return () => clearInterval(timer);
  }, [load, stalled]);

  useEffect(() => {
    if (otherUid) app.backend.getUser(otherUid).then(setOther).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherUid]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.();
  }, [messages.length]);

  // A failure people can't see is a failure they blame on themselves.
  useEffect(() => {
    if (error || refused) alertRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [error, refused]);

  /**
   * Reserve exactly as much room as the composer is currently taking.
   *
   * The composer is fixed and grows UPWARD as things stack into it — a reply
   * quote, an attachment strip, the pick panel, a notice. Anything hidden
   * behind it is the newest message, which is precisely the one somebody just
   * attached a photo to. This started life as three hand-picked pixel
   * constants and they were wrong for the tallest combination by about 200px,
   * which is the usual fate of a magic number that has to predict wrapped text
   * at an unknown font scale. Measuring costs one ResizeObserver.
   *
   * The dependency list is the fallback path: where ResizeObserver does not
   * exist (jsdom, ancient WebViews) a re-measure still runs on every state
   * change that can alter the height.
   */
  useEffect(() => {
    const node = composerRef.current;
    if (!node) return;
    const measure = () => setComposerHeight(node.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [active, attachOpen, attachment, replyTo, composerNotice, loading]);

  useEffect(() => {
    previewRef.current = attachment?.previewUrl ?? null;
  }, [attachment]);

  // Leaving the screen with a photo staged must not leak the blob URL — the
  // document holds it until it is revoked, and this screen is re-entered often.
  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  const retry = () => {
    failures.current = 0;
    setLoading(true);
    setStalled(false); // re-arms the effect, which fetches immediately
  };

  /**
   * How a quoted message reads in a reply.
   *
   * `text` is now legitimately empty on a caption-less photo, so quoting it
   * verbatim produced a blank line that looked like a rendering bug. Name the
   * attachment instead. (The conversation LIST has the same gap and it is not
   * fixable from here: `lastMessagePreview` is derived from the text in
   * functions/src/messaging/messageTriggers.ts.)
   */
  const quoteOf = (m: MessageDoc | null | undefined): string => {
    if (!m) return '…';
    if (m.text.trim()) return m.text.slice(0, 80);
    if (m.media) return m.media.kind === 'video' ? t('msg.media.video') : t('msg.media.photo');
    return '…';
  };

  const clearAttachment = useCallback(() => {
    setAttachment((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
  }, []);

  /**
   * Resize / probe on the DEVICE, before anything is uploaded and before the
   * send button is pressed.
   *
   * Nothing here is a security control — a modified client can skip all of it.
   * It exists so an honest sender gets a specific refusal ("that clip is 22
   * seconds") instead of a failed upload, and so the common case never costs a
   * round trip. A video is NOT re-encoded: it is stored exactly as recorded, so
   * one over the byte cap is refused with instructions to record at a lower
   * quality rather than silently shrunk, which we cannot do.
   *
   * NOTHING IS LOGGED IN HERE. Not the filename, not the error object — a
   * filename is user free text and three leaks of that class have already
   * shipped in this codebase.
   */
  const pickFile = async (file: File, kind: 'image' | 'video') => {
    setComposerNotice('');
    setMediaBusy(true);
    try {
      let prepared: PreparedUpload;
      let previewUrl: string | null = null;
      if (kind === 'image') {
        const image = await preparePostImage(file);
        prepared = {
          kind: 'image',
          blob: image.blob,
          contentType: image.contentType,
          width: image.width,
          height: image.height,
          blurhash: image.blurhash,
          bytes: image.bytes,
        };
        previewUrl = URL.createObjectURL(image.blob);
      } else {
        // probeVideo enforces the 15-second cap and the byte cap, and produces
        // the first-frame blur that a non-Pro recipient will see instead.
        const probe = await probeVideo(file);
        prepared = {
          kind: 'video',
          blob: file,
          contentType: probe.contentType,
          width: probe.width,
          height: probe.height,
          blurhash: probe.blurhash,
          bytes: probe.bytes,
          durationMs: probe.durationMs,
        };
        // The picker's `accept` list is advisory on Android, so a container we
        // cannot store can still arrive here. Caught now rather than as an
        // opaque rejection from storage.rules after the upload has been paid for.
        if (
          checkLimits(
            {
              kind: 'video',
              bytes: prepared.bytes,
              durationMs: prepared.durationMs,
              blurhash: prepared.blurhash,
              contentType: prepared.contentType,
            },
            'media',
          ) === 'content-type-not-allowed'
        ) {
          setComposerNotice(t('msg.media.videoFormat'));
          return;
        }
      }
      clearAttachment();
      setAttachment({ prepared, previewUrl });
      setAttachOpen(false);
    } catch (e) {
      // MediaError messages are written to be shown verbatim: each names the
      // limit and what to do about it. Anything else gets the plain fallback.
      setComposerNotice(e instanceof MediaError ? e.message : t('common.error'));
    } finally {
      setMediaBusy(false);
    }
  };

  /** Every message mutation goes through here: a rejected call must say so, not vanish. */
  const run = async (op: () => Promise<void>): Promise<boolean> => {
    setError('');
    try {
      await op();
      await load();
      return true;
    } catch (e) {
      setError(errText(e));
      return false;
    }
  };

  /**
   * Say whether the attachment actually reached anybody's eyes.
   *
   * `recipientCanView` is the server's answer and the only trustworthy one —
   * this client cannot read the other member's entitlement document. A void
   * result means the backend did not answer at all, which is NOT the same as
   * "yes they can" and must never be rendered as one.
   */
  const announceReach = (result: SentMessage | void) => {
    const reach = (result as SentMessage | undefined)?.media;
    if (!reach) setComposerNotice(t('msg.media.reachUnknown', { name: otherName }));
    else if (!reach.recipientCanView) {
      setComposerNotice(t('msg.media.lockedNow', { name: otherName }));
    }
    // Recipient is Pro and the photo landed. Silence is the right answer — a
    // "delivered!" toast on every send would be noise.
  };

  const send = async () => {
    const text = draft.trim();
    if (!conversationId) return;
    // GUARDED HERE, NOT ONLY ON THE BUTTON, because Enter is a second way to
    // press it and it did not know about `mediaBusy`. A photo is not staged
    // until preparePostImage resolves, so an Enter pressed mid-resize sent the
    // caption ALONE and dropped the picture without a word — the same silent
    // failure this whole screen is built to prevent, arriving through the other
    // door. One guard, both doors, and it says something rather than going dead.
    if (sending) return;
    if (mediaBusy) {
      setComposerNotice(t('msg.media.stillPreparing'));
      return;
    }
    const attached = attachment;
    // A photo with no caption is a normal message; an empty send with neither
    // is the only thing that has nothing to do. Say so rather than sitting
    // there dead — the button stays pressable on purpose.
    if (!text && !attached) {
      setComposerNotice(t('msg.media.needSomething'));
      return;
    }
    const replyRef = replyTo;
    setDraft('');
    setReplyTo(null);
    setError('');
    setRefused('');
    setComposerNotice('');
    setSending(true);
    try {
      // Upload first, send second. The grant callable refuses a non-Pro
      // account, and the send callable requires the moderation receipt those
      // two steps produce — a MediaRef built by hand is rejected server-side.
      let media: MediaRef | undefined = attached?.uploaded;
      if (attached && !media) {
        const uploaded = await app.backend.uploadPostMedia(attached.prepared);
        media = uploaded;
        // Remembered on the staged attachment so a refusal further down (the
        // caption tripping moderation, most often) does not charge the sender a
        // second upload of the same bytes when they reword and press send again.
        setAttachment((current) =>
          current && current.prepared === attached.prepared ? { ...current, uploaded } : current,
        );
      }
      const result = await app.backend.sendMessage(
        conversationId,
        text,
        replyRef ?? undefined,
        media,
      );
      clearAttachment();
      setAttachOpen(false);
      await load();
      if (attached) announceReach(result);
    } catch (e) {
      // Hand the text back rather than eating what someone just wrote. This
      // matters most when moderation refuses it: the message was never stored,
      // so the composer is the only copy left and the point is to let the
      // person REWORD it, not retype it from memory. The staged attachment is
      // deliberately NOT cleared either — re-picking and re-resizing a photo to
      // retry a failed send is the same insult in a different currency.
      setDraft(text);
      setReplyTo(replyRef);
      const refusal = moderationRefusal(e);
      // A refusal is a normal, explainable outcome, so it gets its own quiet
      // banner rather than the red one used for faults — and never an alert(),
      // which the Android WebView draws unstyled and untranslated.
      if (refusal === 'blocked') setRefused(t('msg.moderation.blocked'));
      else if (refusal === 'unavailable') setRefused(t('msg.moderation.unavailable'));
      // Keyed off the machine-readable reason, never the wording: a lapsed
      // subscription is a normal outcome, not a fault, so it gets the quiet
      // banner and keeps everything the sender staged.
      else if (mediaProRequired(e)) setRefused(t('msg.media.proToSend'));
      else setError(errText(e));
    } finally {
      setSending(false);
    }
  };

  const askBlock = () =>
    setPending({
      body: `Block ${other?.displayName ?? 'this user'}? They will not be told.`,
      cta: t('msg.block'),
      act: async () => {
        await app.backend.blockUser(otherUid);
        await app.refreshSocial();
        navigate('/messages');
      },
    });

  const askEnd = () => {
    if (!conversationId) return;
    setPending({
      body: 'End this connection? The thread becomes read-only.',
      cta: t('msg.end'),
      act: async () => {
        await app.backend.endConversation(conversationId);
        await app.refreshSocial();
        navigate('/messages');
      },
    });
  };

  const confirmPending = async () => {
    if (!pending) return;
    setPendingBusy(true);
    setError('');
    try {
      await pending.act();
      setPending(null);
    } catch (e) {
      // Stay open with the reason attached — one tap retries.
      setError(errText(e));
    } finally {
      setPendingBusy(false);
    }
  };

  const doReport = async (reason: ReportReason) => {
    setError('');
    try {
      await app.backend.reportUser(otherUid, reason, `conversation:${conversationId}`);
      setReportOpen(false);
      setMenuOpen(false);
      setNotice('Report filed. Our safety team will review it.');
    } catch (e) {
      setError(errText(e));
    }
  };

  const react = (messageId: string, emoji: string) => {
    setSelected(null);
    return run(() => app.backend.reactToMessage(conversationId!, messageId, emoji));
  };

  const startEdit = (m: MessageDoc) => {
    setEditing(m.id);
    setEditText(m.text);
    setSelected(null);
  };

  const saveEdit = async (messageId: string) => {
    const text = editText.trim();
    if (!text) return;
    // Editor stays open when the call fails so the rewrite isn't lost.
    if (await run(() => app.backend.editMessage(conversationId!, messageId, text))) setEditing(null);
  };

  const removeForMe = (messageId: string) => {
    setSelected(null);
    return run(() => app.backend.deleteMessageForSelf(conversationId!, messageId));
  };

  return (
    <div
      className="screen screen--flush"
      style={{
        // Measured height of the fixed composer, plus the bar it sits above and
        // a little air. The floor keeps the first paint sane, before the
        // composer has been measured at all.
        paddingBottom: `calc(${Math.max(64, Math.round(composerHeight))}px + var(--tabbar-h) + var(--safe-bottom) + 16px)`,
      }}
    >
      <div className="spread" style={{ marginBottom: 12 }}>
        <div className="row">
          <button
            className="btn btn--ghost btn--small"
            aria-label={t('common.back')}
            onClick={() => navigate('/messages')}
          >
            ←
          </button>
          <div className="avatar" aria-hidden>{initialsOf(other?.displayName ?? '?')}</div>
          <div>
            <div className="h3">{other?.displayName ?? '…'}</div>
            <div className="tiny faint">
              {active ? 'Private conversation · Ally never reads this' : t('msg.ended')}
            </div>
          </div>
        </div>
        <button
          className="btn btn--ghost btn--small"
          aria-label="Conversation options"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ⋯
        </button>
      </div>

      {menuOpen && (
        <div className="card col" style={{ marginBottom: 12 }}>
          <button className="btn btn--ghost btn--small" onClick={askBlock}>{t('msg.block')}</button>
          <button
            className="btn btn--ghost btn--small"
            aria-expanded={reportOpen}
            onClick={() => setReportOpen(!reportOpen)}
          >
            {t('msg.report')}
          </button>
          {reportOpen && (
            <div className="wrap">
              {(['harassment', 'spam', 'inappropriate-content', 'safety-concern', 'other'] as ReportReason[]).map((r) => (
                <button key={r} className="chip" onClick={() => doReport(r)}>
                  {r.replace(/-/g, ' ')}
                </button>
              ))}
            </div>
          )}
          <button className="btn btn--danger btn--small" onClick={askEnd}>{t('msg.end')}</button>
          <div className="tiny faint">{t('msg.askAlly')} — a deliberate action; nothing here is analysed automatically.</div>
        </div>
      )}

      <div ref={alertRef}>
        {notice && (
          <div className="banner banner--info small spread" style={{ marginBottom: 12 }}>
            <span>{notice}</span>
            <button className="chip" aria-label={t('common.close')} onClick={() => setNotice('')}>✕</button>
          </div>
        )}
        {error && (
          <div className="banner banner--danger small spread" style={{ marginBottom: 12 }}>
            <span>{error}</span>
            <button className="chip" aria-label={t('common.close')} onClick={() => setError('')}>✕</button>
          </div>
        )}
        {refused && (
          // Info, not danger: the send was refused, nothing broke, and the
          // draft is still in the composer waiting to be reworded.
          <div className="banner banner--info small spread" role="status" style={{ marginBottom: 12 }}>
            <span>{refused}</span>
            <button className="chip" aria-label={t('common.close')} onClick={() => setRefused('')}>✕</button>
          </div>
        )}
        {stalled && (
          <div className="banner banner--danger small spread" style={{ marginBottom: 12 }}>
            <span>Messages stopped updating.</span>
            <button className="chip" onClick={retry}>{t('common.retry')}</button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: '40px 0' }}>
          <div className="spinner" />
        </div>
      ) : messages.length === 0 ? (
        <div className="card muted small center" style={{ lineHeight: 1.6 }}>
          {t('msg.conversationEmpty')}
        </div>
      ) : (
        <div className="chat">
          {messages.map((m) => {
            const mine = m.senderUid === app.uid;
            const reactions = Object.values(m.reactions ?? {});
            const replied = m.replyTo ? messages.find((x) => x.id === m.replyTo) : null;
            const editable =
              mine && Date.now() - Date.parse(m.sentAtISO) < 5 * 60_000 && active;
            const isEditing = editing === m.id;
            return (
              <div
                key={m.id}
                className={`bubble bubble--${mine ? 'user' : 'ally'}`}
                onClick={() => {
                  if (!isEditing) setSelected(selected === m.id ? null : m.id);
                }}
                style={{ cursor: isEditing ? 'default' : 'pointer' }}
              >
                {replied && (
                  <div
                    className="tiny muted"
                    style={{ borderLeft: '2px solid var(--accent-line)', paddingLeft: 8, marginBottom: 6 }}
                  >
                    {quoteOf(replied)}
                  </div>
                )}

                {isEditing ? (
                  // Inline editor: prompt() drew an unstyled system dialog over the
                  // WebView, with none of the app's theme and no way to translate it.
                  <div className="col" onClick={(e) => e.stopPropagation()}>
                    <textarea
                      className="input"
                      rows={2}
                      aria-label="Edit message"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className="row">
                      <button className="btn btn--primary btn--small" onClick={() => saveEdit(m.id)}>
                        {t('common.save')}
                      </button>
                      <button className="btn btn--ghost btn--small" onClick={() => setEditing(null)}>
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {m.text}
                    {m.editedAtISO && <span className="tiny faint"> (edited)</span>}

                    {m.media && (
                      <>
                        {/* `unlocked` decides whether the file is REQUESTED at
                            all — a non-Pro viewer renders the stored blurhash
                            and makes no network call for it. The sender always
                            sees their own attachment whatever their tier, which
                            is exactly what storage.rules says
                            (`isOwner(uid) || isPro()`); locking someone out of
                            their own photo the day their subscription lapses
                            would be gratuitous, and they already have the file. */}
                        <LockedMedia media={m.media} unlocked={isPro || mine} />

                        {m.media.kind === 'video' && (
                          // Said on every clip, to sender and recipient alike.
                          // Nothing checks video, and the image path's check
                          // must never be read as covering one.
                          <div className="tiny faint" style={{ marginTop: 4, lineHeight: 1.45 }}>
                            {t('msg.media.videoUnchecked')}
                          </div>
                        )}

                        {mine && m.mediaLockedFor?.includes(otherUid) && (
                          // The durable half of rule 4: the server recorded, at
                          // send time, that this person could not open it. Past
                          // tense — it is a snapshot, and subscribing later
                          // unlocks everything they were already sent.
                          <div className="tiny gold" style={{ marginTop: 4, lineHeight: 1.45 }}>
                            {t('msg.media.lockedThen', { name: otherName })}
                          </div>
                        )}
                      </>
                    )}

                    {reactions.length > 0 && (
                      <div className="tiny" style={{ marginTop: 4 }}>{reactions.join(' ')}</div>
                    )}
                    <div className="tiny faint" style={{ marginTop: 4, textAlign: mine ? 'right' : 'left' }}>
                      {new Date(m.sentAtISO).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {mine && (m.readBy.filter((u) => u !== app.uid).length > 0 ? ' ✓✓' : ' ✓')}
                    </div>

                    {selected === m.id && active && (
                      <div className="row" style={{ marginTop: 6, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                        {['✨', '❤️', '😄', '🙏'].map((emoji) => (
                          <button key={emoji} className="chip" onClick={() => react(m.id, emoji)}>
                            {emoji}
                          </button>
                        ))}
                        <button
                          className="chip"
                          onClick={() => {
                            setReplyTo(m.id);
                            setSelected(null);
                          }}
                        >
                          ↩ reply
                        </button>
                        {editable && (
                          <button className="chip" onClick={() => startEdit(m)}>✎ edit</button>
                        )}
                        <button className="chip" onClick={() => removeForMe(m.id)}>🗑 for me</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div ref={endRef} />

      {active ? (
        // Column, so the attachment panel stacks above the text row. Inline,
        // because `.composer` owns the fixed positioning and the safe-area
        // padding and neither should be duplicated here.
        <div
          ref={composerRef}
          className="composer"
          style={{ flexDirection: 'column', alignItems: 'stretch' }}
        >
          {replyTo && (
            // Inside the composer rather than floating above it. It used to be
            // a `.float-banner--reply`, pinned by CSS to a fixed 64px above the
            // composer's bottom edge — which was exactly the composer's height
            // until the attachment strip started making it taller, at which
            // point the two drew on top of each other (the banner wins on
            // z-index). Stacking them in one column means neither has to know
            // how tall the other is. NOTE: `.float-banner--reply` in
            // styles/global.css now has no users and can be deleted.
            <div className="banner banner--info small spread">
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Replying to: {quoteOf(messages.find((m) => m.id === replyTo))}
              </span>
              <button
                className="chip"
                style={{ marginLeft: 8 }}
                aria-label={t('common.close')}
                onClick={() => setReplyTo(null)}
              >
                ✕
              </button>
            </div>
          )}
          <ChatAttachPanel
            open={attachOpen}
            isPro={isPro}
            isDemo={app.backend.kind === 'demo'}
            busy={mediaBusy || sending}
            attachment={attachment}
            notice={composerNotice}
            otherName={otherName}
            onFile={pickFile}
            onClear={clearAttachment}
            onDismissNotice={() => setComposerNotice('')}
          />
          <div className="row" style={{ gap: 8 }}>
            <ChatAttachButton
              open={attachOpen}
              busy={mediaBusy}
              onToggle={() => {
                setComposerNotice('');
                setAttachOpen(!attachOpen);
              }}
            />
            <input
              className="input"
              placeholder={t('msg.placeholder')}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                // Editing the refused text is the fix — drop the notice as soon
                // as they start, so it never reads as a standing accusation.
                if (refused) setRefused('');
              }}
              // No guard clause here on purpose — send() owns every one of
              // them, so the key and the button can never drift apart again.
              onKeyDown={(e) => {
                if (e.key === 'Enter') void send();
              }}
            />
            <button
              className="btn btn--primary"
              aria-label="Send message"
              onClick={send}
              // Disabled only while work is genuinely in flight — a send, or a
              // photo still being resized, where a second press would either
              // double-post or send without the attachment. NEVER as a way of
              // expressing an unmet requirement: an empty composer stays
              // pressable and answers with what is missing.
              disabled={sending || mediaBusy}
            >
              {sending ? <span className="spinner" /> : '↑'}
            </button>
          </div>
        </div>
      ) : (
        <div ref={composerRef} className="composer">
          <div className="muted small center" style={{ flex: 1 }}>{t('msg.ended')}</div>
        </div>
      )}

      {pending && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'var(--scrim)', zIndex: 90,
            display: 'grid', placeItems: 'center', padding: 20,
          }}
          onClick={() => {
            if (pendingBusy) return;
            setError('');
            setPending(null);
          }}
        >
          <div
            className="card card--strong col"
            role="dialog"
            aria-modal="true"
            style={{ width: '100%', maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="small" style={{ lineHeight: 1.55 }}>{pending.body}</div>
            {error && <div className="banner banner--danger small">{error}</div>}
            <div className="row">
              <button
                className="btn btn--ghost"
                style={{ flex: 1 }}
                disabled={pendingBusy}
                onClick={() => {
                  setError('');
                  setPending(null);
                }}
              >
                {t('common.cancel')}
              </button>
              <button className="btn btn--danger" style={{ flex: 1 }} disabled={pendingBusy} onClick={confirmPending}>
                {pendingBusy ? <span className="spinner" /> : pending.cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
